import React, { useEffect, useState } from 'react';
import { Badge, Dropdown, List, Button, notification } from 'antd';
import { BellOutlined, ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/ar';
import '../../styles/notifications.css'; // تغيير مسار استيراد ملف CSS
import logger from '../../utils/logger';

const SessionNotifications = ({ events }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // تحويل المواعيد إلى إشعارات
    const now = moment();
    const upcoming = events
      .filter(event => {
        const eventDateTime = moment(`${event.date} ${event.time}`);
        // فلترة المواعيد القادمة خلال الأسبوع القادم
        return eventDateTime.isAfter(now) && eventDateTime.isBefore(moment().add(7, 'days'));
      })
      .map(event => ({
        id: event.id,
        title: event.title,
        date: event.date,
        time: event.time,
        type: event.session_type,
        read: false,
        description: `موعد ${event.session_type}: ${event.title || 'غير محدد'}`
      }));

    setNotifications(upcoming);
    setUnreadCount(upcoming.length);

    // إعداد التنبيهات للمواعيد القادمة
    setupSessionAlerts(upcoming);
  }, [events]);

  const setupSessionAlerts = (sessions) => {
    sessions.forEach(session => {
      const sessionDateTime = moment(`${session.date} ${session.time}`);
      const now = moment();

      // حساب الوقت المتبقي للجلسة
      const timeUntilSession = sessionDateTime.diff(now, 'minutes');
      
      // إعداد التنبيه قبل ساعة ونصف
      if (timeUntilSession > 90) {
        const delay90Min = timeUntilSession - 90;
        setTimeout(() => {
          showNotificationAlert(session, 90);
        }, delay90Min * 60 * 1000);
      }

      // إعداد التنبيه قبل نصف ساعة
      if (timeUntilSession > 30) {
        const delay30Min = timeUntilSession - 30;
        setTimeout(() => {
          showNotificationAlert(session, 30);
        }, delay30Min * 60 * 1000);
      }
    });
  };

  const showNotificationAlert = (session, minutes) => {
    // إظهار التنبيه باستخدام الإشعارات المدمجة في المتصفح
    if (Notification.permission === 'granted') {
      new Notification('تذكير بموعد', {
        body: `لديك ${session.session_type} بعد ${minutes} دقيقة`,
        icon: '/favicon.ico'
      });
    }

    // إظهار التنبيه في واجهة المستخدم
    notification.info({
      message: 'تذكير بموعد',
      description: `لديك ${session.session_type} "${session.title}" بعنوان "${session.title}" بعد ${minutes} دقيقة`,
      placement: 'topRight',
      duration: 0,
      rtl: true,
      icon: <ClockCircleOutlined style={{ color: '#1890ff' }} />,
      onClick: () => {
        playNotificationSound();
      }
    });
  };

  const playNotificationSound = () => {
    // إضافة صوت للتنبيه
    const audio = new Audio('/notification-sound.mp3');
    audio.play().catch(e => logger.debug('Error playing sound:', e));
  };

  // طلب إذن الإشعارات عند تحميل المكون
  useEffect(() => {
    if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(note => 
        note.id === notificationId 
          ? { ...note, read: true }
          : note
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(note => ({ ...note, read: true }))
    );
    setUnreadCount(0);
  };

  const showNotification = (session) => {
    notification.info({
      message: 'تذكير بموعد',
      description: `لديك ${session.session_type} بعنوان "${session.title}" في تاريخ ${moment(session.date).format('YYYY/MM/DD')} الساعة ${session.time}`,
      placement: 'topRight',
      duration: 5,
      rtl: true
    });
  };

  const notificationsList = (
    <List
      className="notifications-dropdown"
      style={{ 
        width: '350px', 
        maxHeight: '400px',
        overflow: 'auto',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}
      header={
        <div className="flex justify-between items-center px-4 py-2 border-b">
          <span className="font-bold">الإشعارات</span>
          {unreadCount > 0 && (
            <Button type="link" onClick={markAllAsRead}>
              تعيين الكل كمقروء
            </Button>
          )}
        </div>
      }
      dataSource={notifications}
      renderItem={item => (
        <List.Item
          className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
            !item.read ? 'bg-blue-50' : ''
          } notification-alert`} // إضافة فئة الأنماط الجديدة هنا
          onClick={() => {
            markAsRead(item.id);
            showNotification(item);
          }}
        >
          <List.Item.Meta
            title={
              <div className="flex justify-between">
                <span>{item.title}</span>
                {!item.read && (
                  <Badge status="processing" className="ml-2" />
                )}
              </div>
            }
            description={
              <div className="text-sm text-gray-500">
                <div>{item.description}</div>
                <div className="mt-1">
                  {moment(item.date).format('YYYY/MM/DD')} - {item.time}
                </div>
              </div>
            }
          />
        </List.Item>
      )}
      locale={{ emptyText: 'لا توجد إشعارات' }}
    />
  );

  return (
    <Dropdown 
      overlay={notificationsList} 
      trigger={['click']}
      placement="bottomLeft"
    >
      <Badge count={unreadCount} className="cursor-pointer">
        <Button 
          icon={<BellOutlined />} 
          shape="circle" 
          size="large"
          className="flex items-center justify-center"
        />
      </Badge>
    </Dropdown>
  );
};

export default SessionNotifications;